/* eslint-disable max-lines */
import {
    ObservedHolidays,
    ReferentPriceTypeOptions,
    RouteCodeOptions,
    RouteCodes,
} from '@strata/constants';
import { Checkbox } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { BidInput, NumberInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import Holidays from 'date-holidays';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import * as Yup from 'yup';

import { BulkChangeWarningMessage } from 'components/upcomingTreatments/dialogs/BulkChangeWarningMessage';
import { DefaultReferralExpirationDialog } from 'components/upcomingTreatments/dialogs/DefaultReferralExpirationDialog';
import { ReferralExpirationStrategy, ReferralExpirationStrategyOptions } from 'services/constants';
import { useSetPreferredPharmacyOfferMutation } from 'store/api/goLiveApi';
import { useGetAllSuppliersWithChildrenQuery } from 'store/api/referenceApi';

const Title = 'Set preferred pharmacy offer';

const PreferredPharmacyRouteCodeOptions = RouteCodeOptions.filter(({ value }) =>
    [RouteCodes.ACCEPT, RouteCodes.CONTRACT, RouteCodes.DIRECT, RouteCodes.ROFR].includes(value),
);

export const PreferredPharmacyOfferDialog = ({
    isOpen,
    onClose,
    apiQueryParams,
    treatmentsCount = 1,
    incumbentMapping,
    staggeredAllowed,
}) => {
    const ReferralExpirationStrategyOptionsInUse = ReferralExpirationStrategyOptions.filter(
        ({ value }) =>
            [
                ReferralExpirationStrategy.DEFAULT,
                ReferralExpirationStrategy.ON_DATE,
                ...(staggeredAllowed ? [ReferralExpirationStrategy.STAGGERED] : []),
                ReferralExpirationStrategy.LATEST_AUTH_EXP_DATE,
            ].includes(value),
    );

    const [defaultReferralExpirationDialogOpen, setDefaultReferralExpirationDialogOpen] =
        useState(false);
    const [setPreferredPharmacyOffer] = useSetPreferredPharmacyOfferMutation();

    const { data: allSuppliersData, isFetching: allSuppliersFetching } =
        useGetAllSuppliersWithChildrenQuery();
    const { supplierList, supplierMap } = allSuppliersData ?? {};
    const parentSupplierList =
        supplierList?.filter((supplier) => supplier.parentSupplierNpi === null) ?? [];

    const holidays = new Holidays('US');

    const countWrongNpi = (supplierNpi) =>
        incumbentMapping
            ?.filter(
                (incumbent) =>
                    supplierMap?.[supplierNpi] &&
                    !supplierMap[supplierNpi].children.includes(incumbent?.incumbent_supplier_npi),
            )
            .reduce(
                (totalTreatmentCount, incumbent) => totalTreatmentCount + incumbent.treatment_count,
                0,
            );

    const parentOrChildSupplierNpiPredicate = (query, supplier) => {
        const queryNorm = query?.toString().toLowerCase() ?? '';

        return (
            supplier?.name.toLowerCase().includes(queryNorm) ||
            supplier?.secondaryData.toLowerCase().includes(queryNorm) ||
            supplier?.children.some((childNpi) => childNpi.includes(queryNorm))
        );
    };

    const isObservedHoliday = (date) => {
        const h = holidays.isHoliday(date);
        const holiday = Array.isArray(h) ? h[0] : h;

        return !!holiday && Object.values(ObservedHolidays).includes(holiday.name);
    };

    return (
        <>
            <DefaultReferralExpirationDialog
                isOpen={!!defaultReferralExpirationDialogOpen}
                preferredPharmacyOffer={defaultReferralExpirationDialogOpen?.preferredPharmacyOffer}
                treatmentsCount={treatmentsCount}
                onClose={(primaryButtonClicked) => {
                    setDefaultReferralExpirationDialogOpen(false);

                    if (primaryButtonClicked) {
                        onClose();
                    }
                }}
            />
            <Formik
                initialValues={{
                    grandfathered: false,
                    useContractRate: false,
                }}
                enableReinitialize
                validationSchema={Yup.object({
                    grandfathered: Yup.bool(),
                    supplierNpi: Yup.string().when('grandfathered', {
                        is: true,
                        then: (schema) => schema.notRequired(),
                        otherwise: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                    transitionSupplier: Yup.string(),
                    routeCode: Yup.string().when(['grandfathered', 'transitionSupplier'], {
                        is: (grandfathered, transitionSupplier) =>
                            !grandfathered || !!transitionSupplier,
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                    referentPriceType: Yup.string(),
                    offerRate: BidInput.validator().when('referentPriceType', {
                        is: (referentPriceType) => !!referentPriceType,
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                    useContractRate: Yup.bool(),
                    forceTransferRoute: Yup.bool(),
                    referralExpirationStrategy: Yup.string().when('grandfathered', {
                        is: (grandfathered) => grandfathered,
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                    referralExpirationReferenceDate: DateInputFormik.validator()
                        .when('referralExpirationStrategy', {
                            is: ReferralExpirationStrategy.ON_DATE,
                            then: (schema) =>
                                schema
                                    .required(validationStrings.requiredField.errorMessage)
                                    .min(
                                        dayjs().startOf('day').toDate(),
                                        'Date must be in the future',
                                    ),
                        })
                        .when('referralExpirationStrategy', {
                            is: ReferralExpirationStrategy.STAGGERED,
                            then: (schema) =>
                                schema
                                    .required(validationStrings.requiredField.errorMessage)
                                    .min(
                                        dayjs().startOf('day').toDate(),
                                        'Date must be in the future',
                                    )
                                    .test('is-not-weekend', 'Weekends are not allowed', (value) => {
                                        if (!value) return true;
                                        const day = dayjs(value).day();
                                        return day !== 0 && day !== 6;
                                    })
                                    .test('is-not-holiday', 'Holidays are not allowed', (value) => {
                                        if (!value) return true;
                                        return !isObservedHoliday(value);
                                    }),
                        }),
                    expireInSetsOf: Yup.number().when('referralExpirationStrategy', {
                        is: (referralExpirationStrategy) =>
                            referralExpirationStrategy === ReferralExpirationStrategy.STAGGERED,
                        then: (schema) =>
                            schema
                                .required(validationStrings.requiredField.errorMessage)
                                .positive(validationStrings.number.positiveNumbersOnly),
                    }),
                })}
                onSubmit={async (values, { resetForm }) => {
                    const preferredPharmacyOffer = {
                        ...values,
                        ...apiQueryParams,
                        offerRate:
                            typeof values.offerRate === 'number'
                                ? Decimal.div(values.offerRate, 100).toNumber()
                                : undefined,
                    };

                    if (values.referralExpirationStrategy === ReferralExpirationStrategy.DEFAULT) {
                        setDefaultReferralExpirationDialogOpen({
                            preferredPharmacyOffer,
                        });
                        return;
                    }

                    await setPreferredPharmacyOffer(preferredPharmacyOffer).unwrap();
                    SuccessToast({ message: 'Preferred pharmacy offer set successfully.' });
                    resetForm();
                    onClose();
                }}
            >
                {(formik) => (
                    <DialogWithForm
                        dialogTitle={Title}
                        isOpen={isOpen}
                        width={560}
                        secondaryButtonLabel="Cancel"
                        onSecondaryButtonClick={() => {
                            formik.resetForm();
                            onClose();
                        }}
                        primaryButtonLabel="Save Changes"
                        isPrimaryButtonDisabled={
                            !(formik.dirty && formik.isValid) || formik.isSubmitting
                        }
                        dialogContent={
                            <>
                                <Checkbox
                                    id="grandfathered"
                                    label="Grandfathered"
                                    onChange={(checked) =>
                                        formik.setValues({
                                            ...formik.values,
                                            grandfathered: checked,
                                            supplierNpi: undefined,
                                            transitionSupplier: undefined,
                                            routeCode: undefined,
                                            referentPriceType: undefined,
                                            offerRate: undefined,
                                            forceTransferRoute: false,
                                            referralExpirationStrategy: undefined,
                                            referralExpirationReferenceDate: undefined,
                                            expireInSetsOf: undefined,
                                        })
                                    }
                                    checked={formik.values.grandfathered ?? false}
                                />
                                {!formik.values.grandfathered && (
                                    <>
                                        <SuggestSingleSelectFormik
                                            id="supplierNpi"
                                            label="Pharmacy"
                                            required={!formik.values.grandfathered}
                                            isLoading={allSuppliersFetching}
                                            values={parentSupplierList}
                                            selectedItem={parentSupplierList?.find(
                                                ({ npi }) => npi === formik.values.supplierNpi,
                                            )}
                                            onChange={(newValue) =>
                                                formik.setFieldValue('supplierNpi', newValue?.npi)
                                            }
                                            itemPredicate={parentOrChildSupplierNpiPredicate}
                                        />
                                        {formik.values.supplierNpi != null &&
                                            countWrongNpi(formik.values.supplierNpi) !== 0 && (
                                                <div className="margin__bottom--medium">
                                                    <InlineMessage
                                                        intent={InlineMessage.Intent.WARNING}
                                                        label={`The selected pharmacy is NOT the incumbent pharmacy for ${countWrongNpi(
                                                            formik.values.supplierNpi,
                                                        )} of the upcoming treatment(s)`}
                                                        wrapText
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                                {formik.values.grandfathered && (
                                    <>
                                        <SuggestSingleSelectFormik
                                            id="referralExpirationStrategy"
                                            label="Referral Expiration Strategy"
                                            required
                                            values={ReferralExpirationStrategyOptionsInUse}
                                            selectedItem={ReferralExpirationStrategyOptions.find(
                                                ({ value }) =>
                                                    value ===
                                                    formik.values.referralExpirationStrategy,
                                            )}
                                            onChange={(newValue) => {
                                                formik.setValues({
                                                    ...formik.values,
                                                    referralExpirationStrategy: newValue?.value,
                                                    referralExpirationReferenceDate: undefined,
                                                    expireInSetsOf: undefined,
                                                });
                                            }}
                                        />
                                        {formik.values.referralExpirationStrategy ===
                                            ReferralExpirationStrategy.ON_DATE && (
                                            <DateInputFormik
                                                id="referralExpirationReferenceDate"
                                                label="Referral Expiration Date"
                                                required
                                                minDate={dayjs().startOf('day').toDate()}
                                            />
                                        )}
                                        {formik.values.referralExpirationStrategy ===
                                            ReferralExpirationStrategy.STAGGERED && (
                                            <>
                                                <DateInputFormik
                                                    id="referralExpirationReferenceDate"
                                                    label="Starting on"
                                                    required
                                                    minDate={dayjs().startOf('day').toDate()}
                                                    dayPickerProps={{
                                                        disabled: [
                                                            { dayOfWeek: [0, 6] },
                                                            isObservedHoliday,
                                                        ],
                                                    }}
                                                />
                                                <NumberInputFormik
                                                    id="expireInSetsOf"
                                                    label="in sets of"
                                                    step={1}
                                                    maxDecimalPlaces={0}
                                                    required
                                                />
                                            </>
                                        )}
                                        <SuggestSingleSelectFormik
                                            id="transitionSupplier"
                                            label="Transition supplier"
                                            isLoading={allSuppliersFetching}
                                            values={parentSupplierList}
                                            selectedItem={parentSupplierList?.find(
                                                ({ npi }) =>
                                                    npi === formik.values.transitionSupplier,
                                            )}
                                            onChange={(newValue) => {
                                                formik.setFieldValue(
                                                    'transitionSupplier',
                                                    newValue?.npi,
                                                );
                                            }}
                                            itemPredicate={parentOrChildSupplierNpiPredicate}
                                        />
                                    </>
                                )}
                                {(!formik.values.grandfathered ||
                                    formik.values.transitionSupplier != null) && (
                                    <>
                                        <SuggestSingleSelectFormik
                                            id="routeCode"
                                            label="Method"
                                            required
                                            values={PreferredPharmacyRouteCodeOptions}
                                            selectedItem={PreferredPharmacyRouteCodeOptions.find(
                                                ({ value }) => value === formik.values.routeCode,
                                            )}
                                            onChange={(newValue) =>
                                                formik.setValues({
                                                    ...formik.values,
                                                    routeCode: newValue?.value,
                                                    referentPriceType: undefined,
                                                    offerRate: undefined,
                                                    useContractRate:
                                                        newValue?.value === RouteCodes.CONTRACT,
                                                })
                                            }
                                        />
                                        {!formik.values.useContractRate && (
                                            <>
                                                <RadioButtonGroupFormik
                                                    name="referentPriceType"
                                                    label="Referent"
                                                    options={ReferentPriceTypeOptions}
                                                    onChange={(newValue) =>
                                                        formik.setValues({
                                                            ...formik.values,
                                                            referentPriceType: newValue,
                                                            offerRate: undefined,
                                                        })
                                                    }
                                                />
                                                <BidInput
                                                    id="offerRate"
                                                    label="Offer Rate"
                                                    required={!!formik.values.referentPriceType}
                                                    referentPriceType={
                                                        formik.values.referentPriceType
                                                    }
                                                />
                                            </>
                                        )}
                                        <Checkbox
                                            id="forceTransferRoute"
                                            label="Ignore Routing"
                                            onChange={(checked) =>
                                                formik.setValues({
                                                    ...formik.values,
                                                    forceTransferRoute: checked,
                                                })
                                            }
                                            checked={formik.values.forceTransferRoute}
                                        />
                                        <Checkbox
                                            id="useContractRate"
                                            label="Use Contract Rate"
                                            onChange={(checked) =>
                                                formik.setValues({
                                                    ...formik.values,
                                                    useContractRate: checked,
                                                    referentPriceType: undefined,
                                                    offerRate: undefined,
                                                })
                                            }
                                            checked={formik.values.useContractRate}
                                        />
                                    </>
                                )}

                                <BulkChangeWarningMessage
                                    apiQueryParams={apiQueryParams}
                                    treatmentsCount={treatmentsCount}
                                />
                            </>
                        }
                    />
                )}
            </Formik>
        </>
    );
};

PreferredPharmacyOfferDialog.Title = Title;

PreferredPharmacyOfferDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    apiQueryParams: PropTypes.shape({
        treatmentId: PropTypes.string,
        searchText: PropTypes.string,
        goLiveDate: PropTypes.arrayOf(PropTypes.string),
        productServiceOid: PropTypes.arrayOf(PropTypes.string),
        segmentId: PropTypes.arrayOf(PropTypes.string),
        incumbentSupplier: PropTypes.arrayOf(PropTypes.string),
        productType: PropTypes.arrayOf(PropTypes.string),
        isLDD: PropTypes.arrayOf(PropTypes.bool),
        hasIssues: PropTypes.arrayOf(PropTypes.string),
        releaseStatus: PropTypes.arrayOf(PropTypes.string),
        upcomingTreatmentBatchName: PropTypes.arrayOf(PropTypes.string),
        preferredPharmacyOffer: PropTypes.arrayOf(PropTypes.string),
        therapeuticCategory: PropTypes.arrayOf(PropTypes.string),
        sort: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                desc: PropTypes.bool.isRequired,
            }),
        ),
    }).isRequired,
    treatmentsCount: PropTypes.number,
    incumbentMapping: PropTypes.arrayOf(
        PropTypes.shape({
            incumbent_supplier_npi: PropTypes.string,
            treatment_count: PropTypes.number,
        }),
    ).isRequired,
    staggeredAllowed: PropTypes.bool.isRequired,
};
